import { makeStyles } from '@material-ui/styles'
import { listTablePrefs } from 'app/constants'
import { listResmgrHosts } from 'app/plugins/openstack/components/infrastructure/actions'
import { getDnsResourceStatus } from 'app/plugins/openstack/components/networks/dns/helper'
import { listRecords } from 'app/plugins/openstack/components/networks/dns/records/actions'
import RadioDropdownFilter from 'app/plugins/openstack/components/networks/dns/records/RadioDropdownFilter'
import RecordCard from 'app/plugins/openstack/components/networks/dns/records/RecordCard'
import { DnsRecordType } from 'app/plugins/openstack/components/networks/dns/records/RecordTypePicklist'
import { recordsSelector } from 'app/plugins/openstack/components/networks/dns/records/selectors'
import { listZones } from 'app/plugins/openstack/components/networks/dns/zones/actions'
import { zonesSelector } from 'app/plugins/openstack/components/networks/dns/zones/selectors'
import { useAppSelector } from 'app/store'
import ListContainer from 'core/containers/ListContainer'
import { createGridStatusCell } from 'core/elements/grid/cells/GridStatusCell'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import DataKeys from 'k8s/DataKeys'
import { pick, uniq } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { formatTimestampToLocal } from '../../../vms/helpers'

const defualtParams = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const columns = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'statusDetails',
    label: 'Status',
    CellComponent: createGridStatusCell({
      dataFn: (statusDetails: any) =>
        getDnsResourceStatus(statusDetails?.status, statusDetails?.action),
    }),
  },
  {
    key: 'type',
    label: 'Type',
  },
  {
    key: 'zone_name',
    label: 'Zone',
  },
  {
    key: 'records',
    label: 'Content',
  },
  {
    key: 'ttl',
    label: 'TTL',
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]
const searchTargets = ['name']

const usePrefParams = createUsePrefParamsHook('DnsRecords', listTablePrefs)

const recordTypesItems = Object.values(DnsRecordType).map((type) => ({ label: type, value: type }))

const getFilteredData = ({
  data,
  selectedZones,
  selectedRecordTypes,
  zoneItems,
  recordTypesItems,
}) => {
  if (!data) return []

  const isAllZones = selectedZones?.length === zoneItems?.length
  const isAllTypes = selectedRecordTypes?.length === recordTypesItems?.length
  const selectedZoneIds = selectedZones?.map((zone) => zone?.value)
  const selectedRecordIds = selectedRecordTypes?.map((type) => type?.value)
  const zoneFilteredData = data?.filter((item) => selectedZoneIds?.includes(item?.zone_id))
  const typeFilteredData = data?.filter((item) => selectedRecordIds?.includes(item?.type))

  let filteredData = data

  if (!isAllZones && isAllTypes) {
    // Only filter by zone
    filteredData = zoneFilteredData
  } else if (!isAllTypes && isAllZones) {
    // Only filter by type
    filteredData = typeFilteredData
  } else if (!isAllZones && !isAllTypes) {
    // Filter by both zone and type - find intersection
    filteredData = zoneFilteredData?.filter((item) => typeFilteredData?.includes(item))
  }
  return filteredData
}

const RecordsListPage = () => {
  const classes = useStyles()
  const { params, getParamsUpdater } = usePrefParams(defualtParams)

  const [selectedRecordTypes, setSelectedRecordTypes] = useState(null)
  const [selectedZones, setSelectedZones] = useState(null)

  const { loading, message, reload } = useListAction(listRecords)
  const { loading: loadingHosts } = useListAction(listResmgrHosts, {
    params: {
      orderBy: 'name',
      orderDirection: 'asc',
    },
  })
  const data = useAppSelector(recordsSelector)

  const [filteredData, setFilteredData] = useState<any[]>(data)

  const { loading: loadingZones } = useListAction(listZones)
  const zones = useAppSelector(zonesSelector)

  const zoneItems = useMemo(() => zones?.map((zone) => ({ label: zone?.name, value: zone?.id })), [
    zones,
  ])

  const typeItmes = useMemo(
    () => uniq(data?.map((record) => ({ label: record?.type, value: record?.type }))),
    [data],
  )

  const handleApplyFilter = useCallback(() => {
    const filteredData = getFilteredData({
      data,
      selectedZones,
      selectedRecordTypes,
      zoneItems,
      recordTypesItems,
    })
    setFilteredData(filteredData)
  }, [data, selectedZones, selectedRecordTypes, zoneItems, recordTypesItems, setFilteredData])

  const headerActions = useMemo(
    () => (
      <div className={classes.filtersContainer}>
        <RadioDropdownFilter
          label="Zone"
          items={zoneItems}
          loading={loadingZones}
          onChange={(selectedItems) => setSelectedZones(selectedItems)}
          handleApplyFilter={handleApplyFilter}
        />
        <RadioDropdownFilter
          label="Record Type"
          items={typeItmes}
          onChange={(selectedItems) => setSelectedRecordTypes(selectedItems)}
          handleApplyFilter={handleApplyFilter}
        />
      </div>
    ),
    [zoneItems, typeItmes, loadingZones, handleApplyFilter],
  )

  return (
    <>
      <ListContainer<any, any>
        dataKey={DataKeys.DnsRecords}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading || loadingHosts}
        loadingMessage={message}
        onRefresh={reload}
        data={filteredData || []}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        CardComponent={RecordCard}
        HeaderActions={headerActions}
        {...pick(listTablePrefs, params)}
        disableRowSelection
      />
    </>
  )
}

export default RecordsListPage

const useStyles = makeStyles<Theme>((theme) => ({
  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
}))
